<template>
    <div class="card breakdown-card border-0 shadow-sm mb-4">
        <div class="card-header bg-white border-0 pt-4 pb-3 px-4">
            <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-2 flex-wrap">
                <h3 class="card-title text-uppercase font-weight-500 mb-0 d-flex align-items-center">
                    <Icon icon-class="ui-file-02" size="3" class="mr-2" />
                    {{ t('invoices-cost_breakdown') }}
                </h3>
                <div v-if="compareInvoice && canCompare && isAdmin" class="compare-badge ml-3">
                    <Icon icon-class="ui-coins-swap-01" size="2" class="mr-1" />
                    {{ t('invoices-comparing_with') }}
                    <strong>{{ compareInvoice.title || ('#' + compareInvoice.id) }}</strong>
                    <KvTooltip :text="t('invoices-remove_comparison')">
                        <button class="compare-badge-close" @click="clearComparison">
                            <Icon icon-class="ui-x-close" size="1" />
                        </button>
                    </KvTooltip>
                </div>
            </div>

            <div class="compare-control" v-if="!editMode && canCompare && isAdmin">
                <div v-if="!showCompareSelector && !compareInvoice">
                    <kv-button variant="outlined" prepend-icon="ui-bar-chart-07" @click="openCompareSelector">
                        {{ t('invoices-compare') }}
                    </kv-button>
                </div>
                <div v-if="showCompareSelector" class="compare-selector-wrap d-flex align-items-center gap-2">
                    <KvSelect
                        v-model="selectedCompareId"
                        :options="compareInvoiceOptions"
                        :disabled="loadingCompare || loadingInvoiceList"
                        :placeholder="loadingInvoiceList ? t('invoices-loading_invoices') : t('invoices-select_invoice')"
                        variant="outlined"
                        filterable
                        class="compare-select-kv"
                        @input="loadCompareInvoice"
                    />
                    <kv-button variant="text" size="small" prepend-icon="ui-x-close" @click="showCompareSelector = false; selectedCompareId = ''"></kv-button>
                </div>
            </div>
            </div>
            <p v-if="compareInvoice" class="compare-changed-hint mb-0 mt-2">{{ t('invoices-compare_changed_hint') }}</p>
        </div>

        <div class="card-body p-0 table-responsive">
            <table class="table custom-table mb-0">
                <thead class="bg-light">
                    <tr v-if="!compareInvoice">
                        <th class="text-left px-4 py-3">{{ t('invoices-description') }}</th>
                        <th class="text-center py-3">{{ t('invoices-quantity') }}</th>
                        <th class="text-right py-3 col-valor">{{ t('invoices-value') }}</th>
                        <th class="text-right pr-4 py-3 col-total">{{ t('invoices-total') }}</th>
                    </tr>
                    <tr v-else>
                        <th class="text-left px-4 py-3">{{ t('invoices-description') }}</th>
                        <th class="text-center py-3">{{ t('invoices-quantity') }} · {{ currentPeriodLabel }}</th>
                        <th class="text-center py-3">{{ t('invoices-quantity') }} · {{ comparePeriodLabel }}</th>
                        <th class="text-right py-3">{{ t('invoices-total') }} · {{ currentPeriodLabel }}</th>
                        <th class="text-right py-3">{{ t('invoices-total') }} · {{ comparePeriodLabel }}</th>
                        <th class="text-right pr-4 py-3">{{ t('invoices-compare_difference') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(row, rIdx) in tableRows"
                        :key="'r-' + rIdx"
                        :class="row.class"
                    >
                        <template v-if="row.type === 'current'">
                            <td class="px-4 align-middle" :class="[compareInvoice ? 'pt-3 pb-1' : 'py-3', { 'sub-item-cell': row.item.isSubItem }]">
                                <div class="item-name font-weight-500" :class="row.item.isGroupHeader ? 'text-dark group-header-name' : (row.item.isSubItem ? 'text-muted sub-item-name' : 'text-dark')">
                                    <template v-if="editMode && row.item.isCustom">
                                        <div class="d-flex align-items-center gap-2">
                                            <KvTooltip :text="t('invoices-remove')">
                                                <button class="btn btn-sm btn-light text-danger p-1 border-0" style="background: transparent;" @click="removeCustom(row.item.customIndex)"><Icon icon-class="ui-trash-01" size="2" /></button>
                                            </KvTooltip>
                                            <input type="text" class="edit-input w-100" style="max-width: none" v-model="row.item.name" :placeholder="t('invoices-description')" @input="onCustomChange(row.item)" />
                                        </div>
                                    </template>
                                    <template v-else>
                                        <span v-if="row.item.isSubItem" class="sub-item-connector mr-2">↳</span>{{ row.item.name }}
                                    </template>
                                </div>
                                <div v-if="row.item.breakdown && !editMode" class="breakdown-badges mt-1">
                                    <KvTooltip :text="getActiveTooltip(row.item)"><span class="breakdown-badge badge-active">{{ row.item.breakdown.active }} {{ t('invoices-active') }}</span></KvTooltip>
                                    <KvTooltip :text="getIncludedTooltip(row.item)"><span class="breakdown-badge badge-included">{{ row.item.breakdown.included }} {{ t('invoices-included') }}</span></KvTooltip>
                                    <KvTooltip :text="getExtraTooltip(row.item)"><span class="breakdown-badge badge-extra">{{ row.item.breakdown.extra }} {{ t('invoices-extra') }}</span></KvTooltip>
                                </div>
                                <div v-if="row.item.channelBreakdown && row.item.channelBreakdown.length" class="breakdown-badges mt-1">
                                    <span v-for="channel in row.item.channelBreakdown" :key="channel.key" class="breakdown-badge badge-channel">
                                        {{ channel.label }}: {{ formatCreditAmount(channel.amount) }}
                                    </span>
                                </div>
                            </td>
                            <td class="text-center val-col align-middle" :class="[compareInvoice ? 'pt-3 pb-1' : 'py-3', { 'font-weight-500': compareInvoice && quantityChanged(row.item) }]">
                                <template v-if="editMode && row.item.quantityField">
                                    <input type="number" class="edit-input text-center" v-model.number="row.item.quantity" min="0" step="1" @input="onItemChange(row.item)" />
                                </template>
                                <span v-else class="text-muted">{{ row.item.displayQuantity != null ? row.item.displayQuantity : (row.item.quantity != null ? row.item.quantity : '-') }}</span>
                            </td>
                            <td v-if="compareInvoice" class="text-center val-col align-middle" :class="[compareInvoice ? 'pt-3 pb-1' : 'py-3', { 'font-weight-500': quantityChanged(row.item) }]">
                                <span class="compare-val text-muted">{{ row.item.compareQty != null ? row.item.compareQty : '—' }}</span>
                            </td>
                            <td v-if="!compareInvoice" class="text-right val-col align-middle" :class="compareInvoice ? 'pt-3 pb-1' : 'py-3'">
                                <template v-if="editMode && row.item.unitPriceField">
                                    <input type="number" class="edit-input text-right" v-model.number="row.item.unitPrice" min="0" step="0.01" @input="onItemChange(row.item)" />
                                </template>
                                <span v-else-if="row.item.showPerUnitValue === false" class="text-muted">—</span>
                                <span v-else class="text-muted">{{ row.item.unitPrice ? formatCurrency(row.item.unitPrice, invoiceCurrency) : '—' }}</span>
                            </td>
                            <td class="text-right val-col align-middle font-weight-500 pr-4" :class="[compareInvoice ? 'pt-3 pb-1' : 'py-3', { 'font-weight-500 text-dark': compareInvoice && Math.abs((Number(row.item.total)||0) - (Number(row.item.compareTotal)||0)) > 0.01 }]">
                                <template v-if="editMode && (row.item.totalField || row.item.isCustom || row.item.isSenderDetail)">
                                    <input type="number" class="edit-input text-right" v-model.number="row.item.total" step="0.01" @input="row.item.isCustom ? onCustomChange(row.item) : onTotalChange(row.item)" />
                                </template>
                                <span v-else>{{ formatCurrency(row.item.total, invoiceCurrency) }}</span>
                            </td>
                            <td v-if="compareInvoice" class="text-right val-col align-middle pr-2" :class="compareInvoice ? 'pt-3 pb-1' : 'py-3'">
                                <span class="compare-val text-muted">{{ formatCurrency(row.item.compareTotal, invoiceCurrency) }}</span>
                            </td>
                            <td v-if="compareInvoice" class="text-right val-col align-middle pr-4" :class="compareInvoice ? 'pt-3 pb-1' : 'py-3'">
                                <span :class="moneyDiff(row.item.total, row.item.compareTotal).className">
                                    {{ moneyDiff(row.item.total, row.item.compareTotal).text }}
                                </span>
                            </td>
                        </template>

                        <td v-else-if="row.type === 'divider'" :colspan="tableColumnCount" class="p-0 border-0">
                            <div class="compare-divider"></div>
                        </td>

                        <td v-else-if="row.type === 'empty'" :colspan="tableColumnCount" class="text-center text-muted py-5 px-4 font-italic">
                            {{ t('invoices-no_detailed_costs_found') }}
                        </td>
                    </tr>
                    <tr v-if="editMode" class="bg-light border-top">
                        <td :colspan="tableColumnCount" class="text-center py-2">
                            <kv-button variant="text" size="small" prepend-icon="ui-plus" @click="addCustom">{{ t('invoices-add_extra_cost') }}</kv-button>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="editableItems.length > 0">
                    <tr class="subtotal-row bg-light border-top">
                        <td colspan="3" class="text-right label-col py-3 font-weight-500 text-dark">{{ t('invoices-subtotal') }}</td>
                        <td class="text-right value-col py-3 font-weight-500 text-dark pr-4" :class="{ 'font-weight-500': compareInvoice && Math.abs(computedSubtotal - compareSubtotal) > 0.01 }">
                            {{ formatCurrency(computedSubtotal, invoiceCurrency) }}
                        </td>
                        <td v-if="compareInvoice" class="text-right value-col py-3 pr-2">
                            <span class="compare-val text-muted">{{ formatCurrency(compareSubtotal, invoiceCurrency) }}</span>
                        </td>
                        <td v-if="compareInvoice" class="text-right value-col py-3 pr-4">
                            <span :class="moneyDiff(computedSubtotal, compareSubtotal).className">{{ moneyDiff(computedSubtotal, compareSubtotal).text }}</span>
                        </td>
                    </tr>

                    <tr v-if="isAdmin || interest_fines > 0" class="interest-row bg-light border-0">
                        <td colspan="3" class="text-right label-col py-3 font-weight-500 text-muted border-bottom-0">
                            {{ t('invoices-interest_fines') }}
                            <div v-if="paymentEdit" class="payment-correction-hint">{{ t('invoices-payment_correction_hint') }}</div>
                        </td>
                        <td class="text-right value-col py-3 font-weight-500 pr-4 border-bottom-0">
                            <template v-if="(editMode || paymentEdit) && isAdmin">
                                <div class="d-flex align-items-center justify-content-end gap-2">
                                    <span class="text-muted">{{ currencySymbol }}</span>
                                    <input type="number" class="edit-input text-right" v-model.number="interest_fines" step="0.01" @input="emitChanges" />
                                </div>
                            </template>
                            <span v-else class="text-muted">{{ formatCurrency(interest_fines, invoiceCurrency) }}</span>
                        </td>
                        <td v-if="compareInvoice" class="text-right value-col py-3 pr-2 border-bottom-0">
                            <span class="compare-val text-muted">{{ formatCurrency(compareInterest, invoiceCurrency) }}</span>
                        </td>
                        <td v-if="compareInvoice" class="text-right value-col py-3 pr-4 border-bottom-0">
                            <span :class="moneyDiff(interest_fines, compareInterest).className">{{ moneyDiff(interest_fines, compareInterest).text }}</span>
                        </td>
                    </tr>

                    <tr v-for="(discount, dIdx) in discounts" :key="'d-' + dIdx" class="discount-row bg-light border-0">
                        <td colspan="3" class="text-right label-col py-2 text-danger font-weight-500">
                            {{ t('invoices-discount_label') }}: {{ discount.description || t('invoices-general') }}
                        </td>
                        <td class="text-right value-col py-2 text-danger font-weight-500 pr-4">
                            - {{ formatCurrency(discount.value || discount.amount, invoiceCurrency) }}
                        </td>
                        <td v-if="compareInvoice" class="border-bottom-0"></td>
                        <td v-if="compareInvoice" class="border-bottom-0"></td>
                    </tr>

                    <tr class="total-row tr-primary-bg" :class="{ 'border-bottom-0': invoice.status === 'paid' }">
                        <td colspan="3" class="text-right label-col py-4 total-footer-label text-dark" :class="{ 'border-bottom-0': invoice.status === 'paid' }">{{ t('invoices-invoice_total') }}</td>
                        <td class="text-right value-col py-4 total-footer-value pr-4" :class="{ 'border-bottom-0': invoice.status === 'paid' }">
                            <div class="current-total-main" :class="{ 'font-weight-500': compareInvoice && Math.abs(computedTotal - compareTotalValue) > 0.01 }">{{ formatCurrency(computedTotal, invoiceCurrency) }}</div>
                        </td>
                        <td v-if="compareInvoice" class="text-right value-col py-4 pr-2" :class="{ 'border-bottom-0': invoice.status === 'paid' }">
                            <span class="compare-total-val text-muted">{{ formatCurrency(compareTotalValue, invoiceCurrency) }}</span>
                        </td>
                        <td v-if="compareInvoice" class="text-right value-col py-4 pr-4" :class="{ 'border-bottom-0': invoice.status === 'paid' }">
                            <span :class="moneyDiff(computedTotal, compareTotalValue).className">{{ moneyDiff(computedTotal, compareTotalValue).text }}</span>
                        </td>
                    </tr>

                    <tr v-if="isAdmin || displayTotalPaid !== null" class="total-paid-row tr-success-bg border-bottom-0">
                        <td colspan="3" class="text-right label-col py-4 total-footer-label text-success font-weight-500 border-bottom-0">{{ t('invoices-total_paid') }}</td>
                        <td class="text-right value-col py-4 total-footer-value pr-4 text-success border-bottom-0">
                            <template v-if="(editMode || paymentEdit) && isAdmin">
                                <div class="d-flex align-items-center justify-content-end gap-2">
                                    <span class="text-success font-weight-500">{{ currencySymbol }}</span>
                                    <input type="number" class="edit-input text-right border-success text-success font-weight-500" v-model.number="displayTotalPaid" step="0.01" @input="emitChanges" />
                                </div>
                            </template>
                            <span v-else class="font-weight-500">{{ displayTotalPaid !== null ? formatCurrency(displayTotalPaid, invoiceCurrency) : '-' }}</span>
                        </td>
                        <td v-if="compareInvoice" class="border-bottom-0"></td>
                        <td v-if="compareInvoice" class="border-bottom-0"></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script>
import keviApi from '../../keviApi';
import { formatCurrency, getCurrencySymbol } from '../helpers';
import Icon from "../../components/icon/icon.vue";
import KvSelect from "../../components/inputs/kv-select.vue";
import KvTooltip from "../../components/ui/kv-tooltip.vue";
import Papa from "papaparse";
import saveData from "../../helpers/saveData";

export default {
    name: "invoice-cost-breakdown",
    components: { Icon, KvSelect, KvTooltip },
    props: {
        invoice: { type: Object, required: true },
        editMode: { type: Boolean, default: false },
        paymentEdit: { type: Boolean, default: false },
        isAdmin: { type: Boolean, default: false }
    },
    data() {
        return {
            editableItems: [],
            customAdjustments: [],
            interest_fines: 0,
            total_paid: null,
            showCompareSelector: false,
            selectedCompareId: '',
            compareInvoice: null,
            compareItems: [],
            otherInvoices: [],
            loadingInvoiceList: false,
            loadingCompare: false,
        };
    },
    watch: {
        invoice: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                if (this.editMode && oldVal !== undefined) return;
                if (newVal) {
                    this.initFromInvoice(newVal);
                    if (newVal.invoice_type !== 'monthly') {
                        this.clearComparison();
                        this.otherInvoices = [];
                    } else if (oldVal && newVal.id !== oldVal.id) {
                        this.otherInvoices = [];
                        this.clearComparison();
                    }
                }
            }
        },
        editMode(val) {
            this.initFromInvoice(this.invoice);
            if (val) this.clearComparison();
        }
    },
    computed: {
        invoiceCurrency() {
            return this.invoice.currency || 'BRL';
        },
        currencySymbol() {
            return getCurrencySymbol(this.invoiceCurrency);
        },
        canCompare() {
            return this.invoice && this.invoice.invoice_type === 'monthly';
        },
        compareInvoiceOptions() {
            if (this.loadingInvoiceList) return [];
            if (this.otherInvoices.length === 0) return [];
            return this.otherInvoices.map(inv => ({
                value: inv.id,
                label: `${inv.title || ('#' + inv.id)} — ${this.formatDateMonth(inv.billing_cycle_start)}`
            }));
        },
        currentInvoiceLabel() {
            if (!this.invoice) return '';
            return this.invoice.title || ('#' + this.invoice.id);
        },
        currentPeriodLabel() {
            return this.formatDateMonth(this.invoice && this.invoice.billing_cycle_start) || this.currentInvoiceLabel;
        },
        comparePeriodLabel() {
            if (!this.compareInvoice) return '';
            return this.formatDateMonth(this.compareInvoice.billing_cycle_start) || (this.compareInvoice.title || ('#' + this.compareInvoice.id));
        },
        tableColumnCount() {
            return this.compareInvoice ? 6 : 4;
        },
        compareInterest() {
            return Number(this.compareInvoice && this.compareInvoice.interest_fines) || 0;
        },
        discounts() {
            const raw = this.invoice?.plan_discounts;
            if (!raw) return [];
            try {
                const d = typeof raw === 'string' ? JSON.parse(raw) : raw;
                return Array.isArray(d) ? d : [];
            } catch (e) { return []; }
        },
        totalDiscount() {
            const subtotal = this.computedSubtotal;
            let remaining = subtotal;
            return this.discounts.reduce((sum, d) => {
                let val = 0;
                if (d.type === 'percentage') {
                    const percent = Number(d.original_value || d.value) || 0;
                    val = remaining * (percent / 100);
                } else {
                    val = Number(d.value || d.amount) || 0;
                }
                val = Math.min(val, remaining);
                remaining -= val;
                return sum + val;
            }, 0);
        },
        computedSubtotal() {
            let sum = this.editableItems.reduce((acc, item) => {
                if (item.isGroupHeader) return acc;
                return acc + (Number(item.total) || 0);
            }, 0);
            sum += this.customAdjustments.reduce((acc, a) => acc + (Number(a.total) || 0), 0);
            return sum;
        },
        computedTotal() {
            return Math.max(0, this.computedSubtotal - this.totalDiscount) + (Number(this.interest_fines) || 0);
        },
        compareSubtotal() {
            return this.compareItems.reduce((sum, item) => {
                if (item.isGroupHeader) return sum;
                return sum + (Number(item.total) || 0);
            }, 0);
        },
        compareDiscountTotal() {
            const raw = this.compareInvoice && this.compareInvoice.plan_discounts;
            let discounts = [];
            try {
                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                discounts = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                discounts = [];
            }
            let remaining = this.compareSubtotal;
            return discounts.reduce((sum, discount) => {
                let value = 0;
                if (discount.type === 'percentage') {
                    const percent = Number(discount.original_value || discount.value) || 0;
                    value = remaining * (percent / 100);
                } else {
                    value = Number(discount.value || discount.amount) || 0;
                }
                value = Math.min(value, remaining);
                remaining -= value;
                return sum + value;
            }, 0);
        },
        compareTotalValue() {
            return Math.max(0, this.compareSubtotal - this.compareDiscountTotal) + this.compareInterest;
        },
        displayTotalPaid: {
            get() {
                if (this.total_paid !== null && this.total_paid !== '') return Number(this.total_paid);
                if (this.invoice.status === 'paid') return Number(this.invoice.total);
                return null;
            },
            set(val) {
                this.total_paid = (val === '' || val === null) ? null : Number(val);
                this.emitChanges();
            }
        },
        mergedItems() {
            const baseItems = Array.isArray(this.editableItems) ? this.editableItems : [];
            const items = baseItems.map((item, idx) => {
                if (!item.isGroupHeader) return item;
                let subTotal = 0;
                for (let j = idx + 1; j < baseItems.length && baseItems[j].isSubItem; j++) {
                    subTotal += Number(baseItems[j].total) || 0;
                }
                return { ...item, total: subTotal };
            });

            this.customAdjustments.forEach((adj, index) => {
               items.push({
                   isCustom: true,
                   customIndex: index,
                   name: adj.description,
                   quantity: null,
                   unitPrice: null,
                   total: adj.total,
                   showPerUnitValue: false
               });
            });

            if (!this.compareInvoice) return items;
            
            const merged = items.map(item => {
                const match = this.compareItems.find(ci => ci.name === item.name && !ci.isGroupHeader);
                const groupMatch = item.isGroupHeader
                    ? this.compareItems.find(ci => ci.name === item.name && ci.isGroupHeader)
                    : null;
                const resolved = groupMatch || match;
                return {
                    ...item,
                    compareQty: resolved ? resolved.quantity : null,
                    compareUnitPrice: resolved ? resolved.unitPrice : null,
                    compareTotal: resolved ? resolved.total : 0,
                    compareShowPerUnit: resolved ? resolved.showPerUnitValue !== false : true,
                };
            });

            const names = new Set(merged.map(item => item.name));
            this.compareItems.forEach(item => {
                if (item.isGroupHeader || names.has(item.name)) return;
                merged.push({
                    name: item.name,
                    quantity: null,
                    unitPrice: null,
                    total: 0,
                    compareQty: item.quantity,
                    compareUnitPrice: item.unitPrice,
                    compareTotal: item.total,
                    compareOnly: true,
                    isSubItem: item.isSubItem,
                    showPerUnitValue: false
                });
            });
            return merged;
        },
        tableRows() {
            const items = this.mergedItems;
            if (items.length === 0) {
                return [{ type: 'empty' }];
            }

            const rows = [];
            items.forEach((item) => {
                rows.push({
                    type: 'current',
                    item: item,
                    class: ['table-row', 'current-row', {
                        'edit-row': this.editMode,
                        'has-compare': !!this.compareInvoice,
                        'group-header-row': item.isGroupHeader,
                        'sub-item-row': item.isSubItem,
                        'row-changed': !!this.compareInvoice && this.rowChanged(item),
                        'row-compare-only': !!item.compareOnly,
                    }]
                });
            });
            return rows;
        }
    },
    methods: {
        initFromInvoice(inv) {
            if (!inv) return;
            
            try {
                let adj = typeof inv.adjustments === 'string' ? JSON.parse(inv.adjustments) : inv.adjustments;
                this.customAdjustments = Array.isArray(adj) ? adj.map(a => ({ description: a.description, total: a.value })) : [];
            } catch (e) {
                this.customAdjustments = [];
            }
            this.interest_fines = Number(inv.interest_fines) || 0;
            this.total_paid = inv.total_paid !== null ? Number(inv.total_paid) : null;

            this.editableItems = this.buildItems(inv);
        },
        buildItems(invData) {
            const i = invData || this.invoice;
            if (!i) return [];
            
            const list = [];
            
            const isPostpaid = i.billing_type === 'postpaid';

            const add = (name, total, qty, qField, uPrice, uField, tField, showPerUnitValue = true) => {
                const t = Number(total) || 0;
                if (t > 0 || this.editMode) {
                    list.push({ 
                        name, 
                        quantity: qty, 
                        quantityField: qField, 
                        unitPrice: uPrice, 
                        unitPriceField: uField, 
                        total: t, 
                        totalField: tField,
                        showPerUnitValue
                    });
                }
            };

            if (i.invoice_type === 'one_time' || i.invoice_type === 'custom') {
                const title = this.chargeLineName(i);
                const val = this.storedChargeAmount(i);
                add(title, val, null, null, null, null, 'plan_base_fee');
            } else {
                add(this.t('invoices-item_base_plan'), i.plan_base_fee, 1, null, i.plan_base_fee, 'plan_base_fee', 'plan_base_fee');
            }
            
            const activeUsers = Number(i.invoice_active_users || 0);
            const includedUsers = Number(i.invoice_included_users || i.plan_included_users || 0);
            const extraUsersTotal = Number(i.invoice_total_for_extra_users ?? i.invoice_total_for_active_users ?? 0);
            const extraUserCount = Math.max(0, activeUsers - includedUsers);
            if (activeUsers > 0 || this.editMode) {
                list.push({
                    name: this.t('invoices-item_users'),
                    breakdown: (activeUsers > 0 || includedUsers > 0) ? { active: activeUsers, included: includedUsers, extra: extraUserCount } : null,
                    displayQuantity: activeUsers,
                    quantity: extraUserCount,
                    quantityField: 'invoice_active_users',
                    quantityOffset: includedUsers,
                    unitPrice: i.plan_price_per_user,
                    unitPriceField: 'plan_price_per_user',
                    total: extraUsersTotal,
                    totalField: 'invoice_total_for_extra_users',
                    showPerUnitValue: true
                });
            }

            const activeBranches = Number(i.invoice_active_branches || 0);
            const includedBranches = Number(i.invoice_included_branches ?? i.plan_included_branches ?? 0);
            const extraBranchesTotal = Number(i.invoice_total_for_extra_branches ?? i.invoice_total_for_active_branches ?? 0);
            const extraBranchCount = Math.max(0, activeBranches - includedBranches);
            if (activeBranches > 0 || this.editMode) {
                list.push({
                    name: this.t('invoices-item_branches'),
                    breakdown: (activeBranches > 0 || includedBranches > 0) ? { active: activeBranches, included: includedBranches, extra: extraBranchCount } : null,
                    displayQuantity: activeBranches,
                    quantity: extraBranchCount,
                    quantityField: 'invoice_active_branches',
                    quantityOffset: includedBranches,
                    unitPrice: i.plan_price_per_branch,
                    unitPriceField: 'plan_price_per_branch',
                    total: extraBranchesTotal,
                    totalField: 'invoice_total_for_extra_branches',
                    showPerUnitValue: true
                });
            }

            const senderDetails = Array.isArray(i.invoice_senders_details) ? i.invoice_senders_details : [];
            if (senderDetails.length > 0) {
                const totalSenders = senderDetails.reduce((sum, s) => sum + (Number(s.price) || 0), 0);
                list.push({
                    name: this.t('invoices-item_integrations'),
                    isGroupHeader: true,
                    quantity: senderDetails.length,
                    total: totalSenders,
                    showPerUnitValue: false,
                });
                senderDetails.forEach((sender, index) => {
                    list.push({
                        name: sender.name,
                        isSubItem: true,
                        quantity: 1,
                        quantityField: null,
                        unitPrice: sender.price,
                        unitPriceField: null,
                        total: sender.price,
                        totalField: null,
                        showPerUnitValue: true,
                        isSenderDetail: true,
                        senderDetailIndex: index
                    });
                });
            } else {
                add(this.t('invoices-item_integrations'), i.invoice_total_for_senders, i.invoice_active_senders, 'invoice_active_senders', i.plan_price_per_integration, 'plan_price_per_integration', 'invoice_total_for_senders');
            }

            const clientsQty = Number(i.invoice_active_clients || 0);
            const clientsUnit = isPostpaid && clientsQty > 0
                ? Math.round((Number(i.invoice_total_for_active_clients) / clientsQty) * 10000) / 10000
                : null;
            add(this.t('invoices-item_contacts'), i.invoice_total_for_active_clients, clientsQty, 'invoice_active_clients', clientsUnit, null, 'invoice_total_for_active_clients', isPostpaid);
            
            add(this.t('invoices-item_credits'), i.invoice_total_for_credits, i.invoice_credits, null, null, null, 'invoice_total_for_credits');
            const creditsRow = list[list.length - 1];
            if (creditsRow && creditsRow.totalField === 'invoice_total_for_credits') {
                creditsRow.channelBreakdown = this.channelBreakdown(i.invoice_credits_by_channel);
            }
            add(this.t('invoices-item_extra_credits'), i.invoice_total_for_extra_credits, i.invoice_extra_credits, null, null, null, 'invoice_total_for_extra_credits');
            add(this.t('invoices-item_incentivized_sales_fee'), i.invoice_total_for_incentivized_revenue, null, null, null, null, 'invoice_total_for_incentivized_revenue');
            add(this.t('invoices-item_retention_fee'), i.invoice_total_for_retention_revenue, null, null, null, null, 'invoice_total_for_retention_revenue');

            return list;
        },

        async openCompareSelector() {
            this.showCompareSelector = true;
            if (this.otherInvoices.length > 0) return;
            
            this.loadingInvoiceList = true;
            try {
                const filters = {
                    places_id: this.invoice.places_id || this.invoice.place_id
                };
                
                if (this.invoice.places_branches_id) {
                    filters.places_branches_id = this.invoice.places_branches_id;
                }

                const opts = keviApi.getOptions(`invoices/admin`, 'GET', { 
                    filters: filters, 
                    per_page: 50 
                });
                const res = await this.$http(opts);
                if (res.status === 200 && res.body) {
                    const data = res.body.data || res.body;
                    this.otherInvoices = (Array.isArray(data) ? data : []).filter(
                        inv => inv.id !== this.invoice.id && inv.invoice_type === 'monthly'
                    );
                }
            } catch (e) {
                console.error('Error fetching invoices:', e);
            } finally {
                this.loadingInvoiceList = false;
            }
        },

        async loadCompareInvoice() {
            if (!this.selectedCompareId) return;
            this.loadingCompare = true;
            try {
                const opts = keviApi.getOptions(`invoices/${this.selectedCompareId}/admin`, 'GET');
                const res = await this.$http(opts);
                if (res.status === 200 && res.body && res.body.data) {
                    const other = res.body.data;
                    if (other.invoice_type !== 'monthly') {
                        this.$store.dispatch(
                            'notification/error',
                            this.t('invoices-comparison_only_monthly_error')
                        );
                        this.selectedCompareId = '';
                        return;
                    }
                    this.compareInvoice = other;
                    this.compareItems = this.buildItems(other);
                    this.appendAdjustments(this.compareItems, other);
                    this.showCompareSelector = false;
                }
            } catch (e) {
                console.error('Erro ao carregar fatura comparada:', e);
            } finally {
                this.loadingCompare = false;
            }
        },

        clearComparison() {
            this.compareInvoice = null;
            this.compareItems = [];
            this.selectedCompareId = '';
            this.showCompareSelector = false;
        },

        onItemChange(item) {
            if (item.quantityField && item.unitPriceField) {
                item.total = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
            } else if (item.unitPriceField && !item.quantityField) {
                item.total = Number(item.unitPrice) || 0;
            }
            if (item.breakdown && item.quantityOffset != null) {
                const newActive = Number(item.quantity) + Number(item.quantityOffset);
                item.breakdown = { ...item.breakdown, active: newActive, extra: Number(item.quantity) };
                item.displayQuantity = newActive;
            }
            this.emitChanges();
        },

        onCustomChange(item) {
            if (item.isCustom && item.customIndex !== undefined) {
                this.customAdjustments[item.customIndex].description = item.name;
                this.customAdjustments[item.customIndex].total = Number(item.total) || 0;
            }
            this.emitChanges();
        },
        removeCustom(index) {
            this.customAdjustments.splice(index, 1);
            this.emitChanges();
        },
        addCustom() {
            this.customAdjustments.push({ description: this.t('invoices-item_cost_adjustment'), total: 0 });
            this.emitChanges();
        },

        onTotalChange(item) {
            if (item.isSenderDetail) {
                item.unitPrice = Number(item.total) || 0;
            }
            if (item.unitPriceField && item.totalField === item.unitPriceField) {
                item.unitPrice = Number(item.total) || 0;
            }
            this.emitChanges();
        },

        emitChanges() {
            const fields = {};
            const sendersDetails = Array.isArray(this.invoice.invoice_senders_details)
                ? JSON.parse(JSON.stringify(this.invoice.invoice_senders_details))
                : [];
            
            let hasSenderDetailsEdit = false;
            let totalSendersCost = 0;

            this.editableItems.forEach(item => {
                if (item.totalField) fields[item.totalField] = Number(item.total) || 0;
                if (item.quantityField) {
                    fields[item.quantityField] = Number(item.quantity) + (Number(item.quantityOffset) || 0);
                }
                if (item.unitPriceField) fields[item.unitPriceField] = Number(item.unitPrice) || 0;

                if (item.isSenderDetail && item.senderDetailIndex !== undefined && sendersDetails[item.senderDetailIndex]) {
                    sendersDetails[item.senderDetailIndex].price = Number(item.unitPrice) || 0;
                    totalSendersCost += Number(item.total) || 0;
                    hasSenderDetailsEdit = true;
                }
            });

            if (hasSenderDetailsEdit) {
                fields.invoice_senders_details = sendersDetails;
                fields.invoice_total_for_senders = totalSendersCost;
            }

            fields.adjustments = this.customAdjustments.map(a => ({
                description: a.description || '',
                value: Number(a.total) || 0
            }));

            if (this.discounts.length > 0) {
                fields.plan_discounts = this.discounts;
            }
            
            if (this.invoice.status === 'paid' || this.isAdmin) {
                fields.interest_fines = Number(this.interest_fines) || 0;
                fields.total_paid = this.total_paid !== null ? Number(this.total_paid) : null;
            }
            
            this.$emit('change', fields);
        },

        storedChargeAmount(invoice) {
            const base = Number(invoice && invoice.plan_base_fee);
            if (Number.isFinite(base) && base > 0) return base;
            const total = Number(invoice && invoice.total);
            return Number.isFinite(total) ? total : 0;
        },
        chargeLineName(invoice) {
            const title = String((invoice && invoice.title) || '')
                .replace(/\s+[—–-]\s+Parcela\s+\d+\/\d+\s*$/i, '')
                .trim();
            return title || this.t('invoices-item_extra_charge');
        },
        channelBreakdown(raw) {
            if (!raw || typeof raw !== 'object') return [];
            return [
                ['whatsapp', 'invoices-credits_whatsapp'],
                ['sms', 'invoices-credits_sms'],
                ['rcs', 'invoices-credits_rcs'],
                ['email', 'invoices-credits_email'],
                ['other', 'invoices-credits_other']
            ]
                .map(([key, label]) => ({ key, label: this.t(label), amount: Number(raw[key]) || 0 }))
                .filter(channel => channel.amount > 0);
        },
        formatCreditAmount(amount) {
            const value = Number(amount) || 0;
            if (Math.abs(value - Math.round(value)) < 0.001) return String(Math.round(value));
            return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
        },
        quantityChanged(item) {
            if (!item || (item.quantity == null && item.compareQty == null)) return false;
            return Math.abs((Number(item.quantity) || 0) - (Number(item.compareQty) || 0)) > 0.001;
        },
        rowChanged(item) {
            if (!item) return false;
            if (item.compareOnly) return true;
            const totalChanged = Math.abs((Number(item.total) || 0) - (Number(item.compareTotal) || 0)) > 0.01;
            if (item.isGroupHeader) return totalChanged;
            return totalChanged || this.quantityChanged(item);
        },
        moneyDiff(current, previous) {
            const diff = (Number(current) || 0) - (Number(previous) || 0);
            if (Math.abs(diff) < 0.01) {
                return { text: this.t('invoices-compare_unchanged'), className: 'diff-equal' };
            }
            const sign = diff > 0 ? '+' : '';
            return {
                text: sign + this.formatCurrency(diff, this.invoiceCurrency),
                className: diff > 0 ? 'diff-higher' : 'diff-lower'
            };
        },
        parseAdjustments(inv) {
            try {
                const adjustments = typeof inv.adjustments === 'string' ? JSON.parse(inv.adjustments) : inv.adjustments;
                return Array.isArray(adjustments)
                    ? adjustments.map(adjustment => ({ description: adjustment.description, total: adjustment.value }))
                    : [];
            } catch (e) {
                return [];
            }
        },
        appendAdjustments(list, inv) {
            this.parseAdjustments(inv).forEach(adjustment => {
                if (!Number(adjustment.total)) return;
                list.push({
                    name: adjustment.description,
                    quantity: null,
                    unitPrice: null,
                    total: adjustment.total,
                    showPerUnitValue: false
                });
            });
        },

        formatCurrency,

        formatDateMonth(dateStr) {
            if (!dateStr) return '—';
            const d = new Date(String(dateStr).replace(' ', 'T'));
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
        },

        exportCsv() {
            const rows = this.mergedItems.map(item => {
                return {
                    [this.t('invoices-description')]: item.name,
                    [this.t('invoices-quantity')]: item.quantity != null ? item.quantity : '-',
                    [this.t('invoices-value')]: item.unitPrice != null ? item.unitPrice : '-',
                    [this.t('invoices-total')]: item.total != null ? item.total : '-'
                };
            });
            rows.push({
                [this.t('invoices-description')]: this.t('invoices-subtotal'),
                [this.t('invoices-quantity')]: '',
                [this.t('invoices-value')]: '',
                [this.t('invoices-total')]: this.computedSubtotal
            });
            if (this.interest_fines > 0) {
                rows.push({
                    [this.t('invoices-description')]: this.t('invoices-interest_fines'),
                    [this.t('invoices-quantity')]: '',
                    [this.t('invoices-value')]: '',
                    [this.t('invoices-total')]: this.interest_fines
                });
            }
            this.discounts.forEach(discount => {
                rows.push({
                    [this.t('invoices-description')]: `${this.t('invoices-discount_label')}: ${discount.description || this.t('invoices-general')}`,
                    [this.t('invoices-quantity')]: '',
                    [this.t('invoices-value')]: '',
                    [this.t('invoices-total')]: `-${discount.value || discount.amount}`
                });
            });
            rows.push({
                [this.t('invoices-description')]: this.t('invoices-invoice_total'),
                [this.t('invoices-quantity')]: '',
                [this.t('invoices-value')]: '',
                [this.t('invoices-total')]: this.computedTotal
            });
            if (this.displayTotalPaid !== null) {
                rows.push({
                    [this.t('invoices-description')]: this.t('invoices-total_paid'),
                    [this.t('invoices-quantity')]: '',
                    [this.t('invoices-value')]: '',
                    [this.t('invoices-total')]: this.displayTotalPaid
                });
            }

            const csv = Papa.unparse(rows);
            const placeName = (this.invoice.place_name || this.invoice.place_legal_name || 'fatura').replace(/[/\\:*?"<>|]/g, '').trim();
            const invoiceTitle = (this.invoice.title || String(this.invoice.id)).replace(/[/\\:*?"<>|]/g, '').trim();
            saveData(csv, `${placeName} - ${invoiceTitle}.csv`);
        },

        getActiveTooltip(item) {
            if (item.quantityField === 'invoice_active_branches') {
                return this.t('invoices-tooltip_active_branches');
            }
            return this.t('invoices-tooltip_active_users');
        },

        getIncludedTooltip(item) {
            if (item.quantityField === 'invoice_active_branches') {
                return this.t('invoices-tooltip_included_branches');
            }
            return this.t('invoices-tooltip_included_users');
        },

        getExtraTooltip(item) {
            if (item.quantityField === 'invoice_active_branches') {
                return this.t('invoices-tooltip_extra_branches');
            }
            return this.t('invoices-tooltip_extra_users');
        }
    }
}
</script>

<style lang="scss" scoped>
.breakdown-card {
    border-radius: 16px;
    border: 1px solid #E5E7EB !important;
    overflow: hidden;

    .card-title {
        font-size: 14px;
        color: #111827;
        letter-spacing: 0.05em;
    }


    .compare-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #EAF7ED;
        color: #2aaa4c;
        border: 1px solid #A7F3D0;
        border-radius: 100px;
        font-size: 12px;
        font-weight: 500;
        padding: 4px 10px 4px 12px;

        strong {
            color: #00542e;
            font-weight: 500;
        }

        .compare-badge-close {
            background: none;
            border: none;
            color: #2aaa4c;
            cursor: pointer;
            padding: 0 0 0 6px;
            font-size: 11px;
            opacity: 0.65;
            transition: opacity 0.15s;

            &:hover {
                opacity: 1;
                color: #00542e;
            }
        }
    }


        .compare-select-kv {
            min-width: 240px;
        }

        .compare-changed-hint {
            padding: 0;
            font-size: 12px;
            color: #6B7280;
        }

        .payment-correction-hint {
            margin-top: 4px;
            font-size: 11px;
            font-weight: 400;
            color: #9CA3AF;
            text-transform: none;
            letter-spacing: 0;
        }

    .custom-table {
        table-layout: fixed;
        width: 100%;
        min-width: 750px;

        th {
            font-size: 12px; font-weight: 500; color: #6B7280;
            text-transform: uppercase; letter-spacing: 0.05em;
            border-top: 1px solid #E5E7EB; border-bottom: 1px solid #E5E7EB;
        }
        
        .col-valor { width: 140px; }
        .col-total { width: 160px; }

        td { 
            border-bottom: 1px solid #F3F4F6; 
            border-left: none !important; 
            border-right: none !important;
        }

        tr {
            border-left: none !important;
            border-right: none !important;
        }

        .table-row:hover td { background-color: #F9FAFB; }
        .row-changed td { background-color: #FFFBEB; }
        .row-changed:hover td { background-color: #FEF3C7; }
        .row-compare-only td { background-color: #F8FAFC; }
        .current-row td { border-bottom: none; }
        .compare-row td { border-bottom: none; }

        .compare-divider-row td { padding: 0; border: none; }
        .compare-divider { height: 1px; background: #E5E7EB; margin: 0 16px; }

        .item-name { font-size: 14px; line-height: 1.2; }
        .val-col { font-size: 14px; }
        
        .row-label {
            font-size: 10px; font-weight: 500; letter-spacing: 0.04em;
            text-transform: uppercase; margin-top: 4px;
            color: #6B7280;
        }

        .compare-val { font-size: 13px; font-weight: 500; }
        .compare-total-val { font-size: 16px; font-weight: 500; }
        .current-total-main { font-size: 20px; font-weight: 500; }

        .total-footer-label {
            font-size: 1.05rem;
            font-weight: 500;
        }
        .total-footer-value {
            font-size: 1.05rem;
            font-weight: 500;
        }

        .diff-badge {
            display: inline-block; font-size: 10px; font-weight: 500;
            border-radius: 6px; padding: 2px 6px;
        }

        .diff-higher { color: #DC2626; .diff-badge { background: #FEE2E2; } }
        .diff-lower { color: #059669; .diff-badge { background: #D1FAE5; } }
        .diff-equal { color: #6B7280; }

        .edit-input {
            width: 90%; max-width: 100px; padding: 4px 8px; border: 1px solid #EAF7ED;
            border-radius: 8px; font-size: 13px; color: #111827; background: #fff;
            outline: none; transition: all 0.15s;
            &:focus { border-color: #2aaa4c; box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.15); }
            &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
            &[type=number] { -moz-appearance: textfield; appearance: none; }
        }

        .edit-row td { background-color: #FAFEFF; }
        .bg-light { background-color: #F9FAFB !important; }
        .tr-primary-bg { background-color: rgba(42, 170, 76, 0.05) !important; }
        .tr-success-bg { background-color: #EAF7ED !important; }

        .group-header-row td {
            background-color: #F9FAFB;
            border-bottom: none !important;
        }
        .row-changed td { background-color: #FFFBEB !important; }
        .row-changed:hover td { background-color: #FEF3C7 !important; }
        .group-header-name { font-size: 14px; }

        .sub-item-row td { border-bottom: 1px solid #F3F4F6; }
        .sub-item-cell { padding-left: 32px !important; }
        .sub-item-name { font-size: 13px; font-weight: 400 !important; }
        .sub-item-connector { color: #9CA3AF; font-size: 12px; }

        .breakdown-badges {
            display: flex;
            align-items: center;
            gap: 5px;
            flex-wrap: wrap;
        }
        .badge-channel { background: #EEF2FF; color: #3730A3; }
        .breakdown-badge {
            display: inline-flex;
            align-items: center;
            font-size: 11px;
            font-weight: 500;
            padding: 2px 7px;
            border-radius: 20px;
            line-height: 1.4;
            cursor: default;
        }
        .badge-active  { background: #F3F4F6; color: #6B7280; }
        .badge-included { background: #F3F4F6; color: #6B7280; }
        .badge-extra   { background: #F3F4F6; color: #6B7280; }
    }
}
</style>
